// src/services/api/endpoints/gatherings.ts
import { Gathering, GatheringPendingEdit, GatheringStatus } from '../../../types';
import {
  API_BASE_URL,
  fetchWithTokenRefresh,
  getApiErrorStatus
} from '../client';
import {
  generateCacheKey,
  getCacheItem,
  setCacheItem,
  invalidateCache
} from '../cache';
import { PaginatedResponse, formatDateForApi } from '../utils';
import { createLimiter } from '../utils/concurrencyLimit';

// Shared 3-wide queue for every outbound /gatherings request — the search
// fanout and the ENG-1097 pending-edit sweep both draw from it.
// The admin dashboard's eager fanout (Board mount + calendar views +
// refreshAllData) was exhausting the backend SQLAlchemy pool and 503'ing
// /ready. With one chokepoint at this module, every status × every page
// request — no matter which UI call site triggered it — competes for
// the same 3 slots, bounding the in-flight burst regardless of how
// many fanout points are running concurrently. See ENG-933.
//
// ENG-1097 added a second kind of caller: /gatherings/search does NOT report
// has_pending_edit (the backend only populates it on the authenticated
// single-gathering read — see ENG-1363), so finding staged edits means one
// request per gathering. That sweep shares THIS limiter rather than getting a
// second 3-wide queue of its own, so the dashboard's total in-flight ceiling
// against the API stays at 3 (decision: Nikki, 2026-08-05). A dedicated queue
// would have doubled the ceiling that the ENG-933 incident established, and
// the backend's SQLAlchemy pool — not dashboard latency — is the scarce
// resource here.
//
// Starvation of the board's render fanout is the tradeoff, and it is small in
// practice: the sweep can only enqueue work once a search has already returned
// the gatherings to sweep, so the render requests are ahead of it in the queue;
// sweep answers are cached (including "none staged"); and an in-flight sweep is
// aborted when the candidate set changes.
const gatheringApiLimiter = createLimiter(3);

// Every pending-edit cache key we have written. The shared cache only supports
// prefix invalidation, and the pending-edit keys sit under the same
// `/gatherings/<id>` prefix as the gatherings themselves, so clearing just the
// pending-edit answers (what a manual re-check wants) needs the key list.
const pendingEditCacheKeys: string[] = [];

const rememberPendingEditCacheKey = (cacheKey: string): void => {
  if (pendingEditCacheKeys.indexOf(cacheKey) === -1) {
    pendingEditCacheKeys.push(cacheKey);
  }
};

/**
 * Drops every cached pending-edit answer so the next lookup hits the API.
 * Used by the board's manual "re-check" so an admin can force a fresh read
 * inside the normal cache window.
 */
export const invalidateAllPendingEditCaches = (): void => {
  pendingEditCacheKeys.forEach((cacheKey) => invalidateCache(cacheKey));
  pendingEditCacheKeys.length = 0;
};

/**
 * Drops every cached view that a pending-edit decision invalidates: the
 * pending edit itself, the gathering (whose live fields may have just changed)
 * and the search listings that render it.
 */
const invalidatePendingEditCaches = (gatheringId: string): void => {
  invalidateCache(`${API_BASE_URL}/gatherings/${gatheringId}`);
  invalidateCache(`${API_BASE_URL}/gatherings/search`);
};

/**
 * Fetches the current pending (staged, awaiting-review) edit for a gathering.
 *
 * `GET /gatherings/{id}/pending-edit` 404s when nothing is staged, which is a
 * normal outcome rather than a failure, so that case resolves to `null`.
 * Retries are disabled: the 404 is the common answer during a sweep and the
 * default 3-retry backoff would turn each "no edit" into ~6s of waiting.
 *
 * @param token Firebase authentication token
 * @param gatheringId The gathering to check
 * @param signal Optional abort signal, honoured while queued and in flight
 * @returns The pending edit, or null when none is staged
 */
export const fetchGatheringPendingEdit = async (
  token: string,
  gatheringId: string,
  signal?: AbortSignal
): Promise<GatheringPendingEdit | null> => {
  const url = `${API_BASE_URL}/gatherings/${gatheringId}/pending-edit`;
  const cacheKey = generateCacheKey(url);

  // `null` is a real, cacheable answer, so distinguish "cached null" from
  // "cache miss" by storing a wrapper rather than the value itself.
  const cached = getCacheItem<{ pendingEdit: GatheringPendingEdit | null }>(cacheKey);
  if (cached) {
    return cached.pendingEdit;
  }

  try {
    const data = await gatheringApiLimiter(
      () =>
        fetchWithTokenRefresh(
          url,
          token,
          signal ? { signal } : {},
          /* retryCount */ 0
        ),
      signal
    );
    setCacheItem(cacheKey, { pendingEdit: data as GatheringPendingEdit });
    rememberPendingEditCacheKey(cacheKey);
    return data as GatheringPendingEdit;
  } catch (error) {
    if (getApiErrorStatus(error) === 404) {
      setCacheItem(cacheKey, { pendingEdit: null });
      rememberPendingEditCacheKey(cacheKey);
      return null;
    }
    // Anything else (403, 5xx, offline) is a real failure the caller must see;
    // silently reporting "no pending edit" would hide unapproved host edits.
    throw error;
  }
};

/**
 * Approves a staged edit, applying its values to the live gathering.
 *
 * Can legitimately fail after the page loaded — the edit may have been
 * withdrawn, re-submitted, or (once ENG-1096 lands) expired or superseded in
 * the meantime — so callers must surface the error rather than assume success.
 *
 * @param token Firebase authentication token
 * @param gatheringId The gathering whose staged edit is being approved
 * @returns The updated gathering, with the staged values now live
 */
export const approveGatheringPendingEdit = async (
  token: string,
  gatheringId: string
): Promise<Gathering> => {
  const url = `${API_BASE_URL}/gatherings/${gatheringId}/pending-edit/approve`;

  const response = await fetchWithTokenRefresh(
    url,
    token,
    { method: 'POST' },
    // No retries: approve is not idempotent from the reviewer's point of view
    // and a 404 here means the edit is already gone, so retrying only delays
    // the error the admin needs to read.
    /* retryCount */ 0
  );

  invalidatePendingEditCaches(gatheringId);

  return response;
};

/**
 * Rejects a staged edit, discarding it without touching the live gathering.
 *
 * @param token Firebase authentication token
 * @param gatheringId The gathering whose staged edit is being rejected
 * @param notes Optional reviewer notes recorded on the rejected edit
 * @returns The updated gathering (unchanged content; the staged edit is gone)
 */
export const rejectGatheringPendingEdit = async (
  token: string,
  gatheringId: string,
  notes?: string | null
): Promise<Gathering> => {
  const url = `${API_BASE_URL}/gatherings/${gatheringId}/pending-edit/reject`;

  const trimmed = typeof notes === 'string' ? notes.trim() : '';

  const response = await fetchWithTokenRefresh(
    url,
    token,
    {
      method: 'POST',
      // `notes` is nullable server-side; send null rather than "" so an empty
      // box does not record a blank reason.
      body: JSON.stringify({ notes: trimmed === '' ? null : trimmed })
    },
    /* retryCount */ 0
  );

  invalidatePendingEditCaches(gatheringId);

  return response;
};

/**
 * Fetches gatherings from the Fabrik API without any filtering
 * @param token Firebase authentication token
 * @returns Promise containing an array of gatherings
 */
export const fetchAllGatherings = async (token: string): Promise<Gathering[]> => {
  try {
    const url = `${API_BASE_URL}/gatherings/search`;
    const cacheKey = generateCacheKey(url);
    
    // Check if we have a valid cached response
    const cachedData = getCacheItem<Gathering[]>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    // fetchWithTokenRefresh already returns the parsed JSON data
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response
    setCacheItem(cacheKey, data.items);
    
    return data.items;
  } catch (error) {
    console.error('Error fetching gatherings:', error);
    throw error;
  }
};

/**
 * Fetches a gathering by its ID
 * @param token Firebase authentication token
 * @param gatheringId The ID of the gathering to fetch
 * @returns Promise containing the gathering
 */
export const fetchGatheringById = async (token: string, gatheringId: string): Promise<Gathering> => {
  try {
    const url = `${API_BASE_URL}/gatherings/${gatheringId}`;
    const cacheKey = generateCacheKey(url);
    
    // Check if we have a valid cached response
    const cachedData = getCacheItem<Gathering>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    // fetchWithTokenRefresh already returns the parsed JSON data
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response
    setCacheItem(cacheKey, data);
    
    return data;
  } catch (error) {
    console.error('Error fetching gathering:', error);
    throw error;
  }
};

/**
 * Fetches gatherings for a specific status
 * @param token Firebase authentication token
 * @param status Gathering status to filter by
 * @param page Page number (default: 1)
 * @param orderBy Field to order by (default: 'date')
 * @param orderDirection Direction to order (default: 'asc')
 * @param fromDate Optional from_date filter (default: null)
 * @param toDate Optional to_date filter (default: null)
 * @param spaceId Optional space_id filter (default: null)
 * @returns Promise containing paginated gatherings
 */
export const fetchGatheringsByStatus = async (
  token: string,
  status: GatheringStatus,
  page: number = 1,
  orderBy: string = 'date',
  orderDirection: string = 'asc',
  fromDate: string | null = null,
  toDate: string | null = null,
  spaceId: string | null = null,
  signal?: AbortSignal
): Promise<PaginatedResponse<Gathering>> => {
  try {
    const searchParams = new URLSearchParams();

    // Add pagination parameters - only page, no limit
    searchParams.append('page', page.toString());

    // Add status parameter
    searchParams.append('status', status);

    // Add sorting parameters
    searchParams.append('order_by', orderBy);
    searchParams.append('order_direction', orderDirection);

    // Add date filters if provided
    if (fromDate) {
      searchParams.append('from_date', fromDate);
    }

    if (toDate) {
      searchParams.append('to_date', toDate);
    }

    // Add space filter if provided
    if (spaceId) {
      searchParams.append('space_id', spaceId);
    }

    const url = `${API_BASE_URL}/gatherings/search?${searchParams.toString()}`;
    const cacheKey = generateCacheKey(url, {
      page,
      orderBy,
      orderDirection,
      status,
      fromDate,
      toDate,
      spaceId
    });

    // Check if we have a valid cached response (cache hits skip the limiter)
    const cachedData = getCacheItem<PaginatedResponse<Gathering>>(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    // Route through the shared limiter (3 concurrent) and forward the abort
    // signal to fetch via RequestInit. The limiter handles "abort while
    // queued" so we don't even start the request if the caller has moved on.
    const data = await gatheringApiLimiter(
      () => fetchWithTokenRefresh(url, token, signal ? { signal } : {}),
      signal
    );

    // Cache the response
    setCacheItem(cacheKey, data);

    return data;
  } catch (error) {
    console.error(`Error fetching ${status} gatherings:`, error);
    throw error;
  }
};

/**
 * Fetches gatherings for a specific month range (for calendar view)
 * This function fetches gatherings across all statuses for a given month
 * @param token Firebase authentication token
 * @param year Year to fetch (e.g., 2025)
 * @param month Month to fetch (1-12)
 * @param spaceId Optional space_id filter
 * @returns Promise containing gatherings for the month
 */
export const fetchGatheringsForMonth = async (
  token: string,
  year: number,
  month: number,
  spaceId: string | null = null,
  signal?: AbortSignal
): Promise<Gathering[]> => {
  try {
    // Calculate first and last day of the month
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0); // Day 0 is the last day of previous month

    // Format dates as YYYY-MM-DD
    const fromDate = formatDateForApi(firstDay);
    const toDate = formatDateForApi(lastDay);

    // Create array to hold all gatherings
    let allGatherings: Gathering[] = [];

    // Fetch gatherings for each status with the date range
    const statusValues = Object.values(GatheringStatus);

    // Promise.all over all 8 statuses still — but each fetch is gated by the
    // shared limiter inside fetchGatheringsByStatus, so only 3 hit the
    // backend at a time across the whole app.
    const results = await Promise.all(
      statusValues.map(status =>
        fetchGatheringsByStatus(
          token,
          status,
          1, // Start with page 1
          'date', // Order by date
          'asc', // Ascending order
          fromDate,
          toDate,
          spaceId,
          signal
        )
      )
    );

    // Combine all results
    results.forEach(result => {
      allGatherings = [...allGatherings, ...result.items];
    });

    return allGatherings;
  } catch (error) {
    console.error('Error fetching gatherings for month:', error);
    throw error;
  }
};

/**
 * Fetches gatherings for a specific week range (for week view)
 * This function fetches gatherings across all statuses for a given week
 * @param token Firebase authentication token
 * @param startDate The first day of the week
 * @param endDate The last day of the week
 * @param spaceId Optional space_id filter
 * @returns Promise containing gatherings for the week
 */
export const fetchGatheringsForWeek = async (
  token: string,
  startDate: Date,
  endDate: Date,
  spaceId: string | null = null,
  signal?: AbortSignal
): Promise<Gathering[]> => {
  try {
    // Format dates as YYYY-MM-DD
    const fromDate = formatDateForApi(startDate);
    const toDate = formatDateForApi(endDate);

    // Create array to hold all gatherings
    let allGatherings: Gathering[] = [];

    // Fetch gatherings for each status with the date range
    const statusValues = Object.values(GatheringStatus);

    // Promise.all over all 8 statuses, throttled by the shared limiter
    // inside fetchGatheringsByStatus to 3 concurrent backend requests.
    const results = await Promise.all(
      statusValues.map(status =>
        fetchGatheringsByStatus(
          token,
          status,
          1, // Start with page 1
          'date', // Order by date
          'asc', // Ascending order
          fromDate,
          toDate,
          spaceId,
          signal
        )
      )
    );

    // Combine all results
    results.forEach(result => {
      allGatherings = [...allGatherings, ...result.items];
    });

    return allGatherings;
  } catch (error) {
    console.error('Error fetching gatherings for week:', error);
    throw error;
  }
};

/**
 * Fetches gatherings by date range without filtering by status
 * This is the appropriate function to use for calendar views
 * @param token Firebase authentication token
 * @param fromDate Start date in YYYY-MM-DD format
 * @param toDate End date in YYYY-MM-DD format
 * @param page Page number (default: 1)
 * @param orderBy Field to order by (default: 'date')
 * @param orderDirection Order direction (default: 'asc')
 * @param spaceId Optional space_id filter
 * @returns Promise containing paginated gatherings
 */
export const fetchGatheringsByDateRange = async (
  token: string,
  fromDate: string,
  toDate: string,
  page: number = 1,
  orderBy: string = 'date',
  orderDirection: string = 'asc',
  spaceId: string | null = null,
  signal?: AbortSignal
): Promise<PaginatedResponse<Gathering>> => {
  try {
    const searchParams = new URLSearchParams();

    // Add pagination parameters
    searchParams.append('page', page.toString());

    // Add sorting parameters
    searchParams.append('order_by', orderBy);
    searchParams.append('order_direction', orderDirection);

    // Add date filters
    searchParams.append('from_date', fromDate);
    searchParams.append('to_date', toDate);

    // Add space filter if provided
    if (spaceId) {
      searchParams.append('space_id', spaceId);
    }

    const url = `${API_BASE_URL}/gatherings/search?${searchParams.toString()}`;
    const cacheKey = generateCacheKey(url, {
      page,
      orderBy,
      orderDirection,
      fromDate,
      toDate,
      spaceId
    });

    // Check if we have a valid cached response (cache hits skip the limiter)
    const cachedData = getCacheItem<PaginatedResponse<Gathering>>(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    // Same shared limiter as fetchGatheringsByStatus — every /gatherings
    // caller shares the one 3-wide queue (see the declaration above).
    const data = await gatheringApiLimiter(
      () => fetchWithTokenRefresh(url, token, signal ? { signal } : {}),
      signal
    );

    // Cache the response
    setCacheItem(cacheKey, data);

    return data;
  } catch (error) {
    console.error(`Error fetching gatherings for date range:`, error);
    throw error;
  }
};

/**
 * Updates the status of a gathering
 * @param token Firebase authentication token
 * @param gatheringId The ID of the gathering to update
 * @param newStatus The new status to set
 * @returns Promise that resolves when the update is complete
 */
export const updateGatheringStatus = async (
  token: string,
  gatheringId: string, 
  newStatus: GatheringStatus
): Promise<void> => {
  if (!token) {
    throw new Error('Authentication token is required');
  }

  try {
    // Invalidate cache for this gathering and any status-based queries
    invalidateCache(`${API_BASE_URL}/gatherings/${gatheringId}`);
    invalidateCache(`${API_BASE_URL}/gatherings/search`);
    
    // Get the enum key (e.g., "IN_REVIEW") from the value ("In Review")
    const statusKey = Object.keys(GatheringStatus).find(
      key => GatheringStatus[key as keyof typeof GatheringStatus] === newStatus
    );
    
    if (!statusKey) {
      throw new Error(`Invalid gathering status: ${newStatus}`);
    }
    
    const url = `${API_BASE_URL}/gatherings/${gatheringId}/status?gathering_status=${statusKey}`;
    
    await fetchWithTokenRefresh(url, token, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    // Success - no need to return anything
  } catch (error: unknown) {
    console.error('Error updating gathering status:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to update gathering status: ${error.message}`);
    } else {
      throw new Error(`Failed to update gathering status: ${String(error)}`);
    }
  }
};

/**
 * Updates a gathering with new data
 * @param token Firebase authentication token
 * @param gatheringId The ID of the gathering to update
 * @param gatheringData The data to update the gathering with
 * @returns Promise containing the updated gathering
 */
export const updateGatheringDetails = async (
  token: string,
  gatheringId: string,
  gatheringData: Partial<Gathering>
): Promise<Gathering> => {
  try {
    const url = `${API_BASE_URL}/gatherings/${gatheringId}`;
    
    // Check if any date/time fields are being updated
    const isUpdatingDateOrTime = 
      gatheringData.date !== undefined || 
      gatheringData.start_time !== undefined || 
      gatheringData.end_time !== undefined;
    
    // If updating any date/time field, ensure all three are included
    if (isUpdatingDateOrTime) {
      // Fetch the current gathering to get any missing date/time fields
      const currentGathering = await fetchGatheringById(token, gatheringId);
      
      // Ensure all three fields are included in the update
      if (gatheringData.date === undefined) {
        gatheringData.date = currentGathering.date;
      }
      if (gatheringData.start_time === undefined) {
        gatheringData.start_time = currentGathering.start_time;
      }
      if (gatheringData.end_time === undefined) {
        gatheringData.end_time = currentGathering.end_time;
      }
    }
    
    // Prepare the data for the API
    const dataToSend: any = {
      ...gatheringData,
      is_compat_mode: true // Required by the API
    };
    
    // Special handling for area_ids if they exist
    if (gatheringData.areas) {
      dataToSend.area_ids = gatheringData.areas.map(area => area.id);
      delete dataToSend.areas;
    }
    
    // Handle date formatting if needed
    if (dataToSend.date && dataToSend.date instanceof Date) {
      dataToSend.date = formatDateForApi(dataToSend.date);
    }
    
    const response = await fetchWithTokenRefresh(url, token, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(dataToSend)
    });
    
    // Invalidate cache for this gathering and any search queries
    invalidateCache(`${API_BASE_URL}/gatherings/${gatheringId}`);
    invalidateCache(`${API_BASE_URL}/gatherings/search`);
    
    return response;
  } catch (error) {
    console.error(`Error updating gathering details:`, error);
    throw error;
  }
};

/**
 * Updates a gathering's schedule (date, time, and areas)
 * @param token Firebase authentication token
 * @param gatheringId The ID of the gathering to update
 * @param scheduleData The new schedule data
 * @returns Promise containing the updated gathering
 */
export const updateGatheringSchedule = async (
  token: string,
  gatheringId: string,
  scheduleData: {
    date: string;
    start_time: string;
    end_time: string;
    space_id: string;
    area_ids: string[];
    duration?: number;
  }
): Promise<Gathering> => {
  try {
    // Convert the schedule data to the format expected by the API
    const dataToSend = {
      date: scheduleData.date,
      start_time: scheduleData.start_time,
      end_time: scheduleData.end_time,
      space_id: scheduleData.space_id,
      area_ids: scheduleData.area_ids,
      duration: scheduleData.duration
    };
    
    const url = `${API_BASE_URL}/gatherings/${gatheringId}`;
    
    // Invalidate cache for this gathering and any related queries
    invalidateCache(`${API_BASE_URL}/gatherings/${gatheringId}`);
    invalidateCache(`${API_BASE_URL}/gatherings/search`);
    
    const response = await fetchWithTokenRefresh(url, token, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(dataToSend)
    });
    
    return response;
  } catch (error) {
    console.error('Error updating gathering schedule:', error);
    throw error;
  }
};

/**
 * Moves a gathering to a new space with new areas
 * @param token Firebase authentication token
 * @param gatheringId The ID of the gathering to move
 * @param moveData The new space and area data
 * @returns Promise containing the updated gathering
 */
export const moveGathering = async (
  token: string,
  gatheringId: string,
  moveData: {
    new_space_id: string;
    new_area_ids: string[];
  }
): Promise<Gathering> => {
  try {
    const url = `${API_BASE_URL}/gatherings/${gatheringId}/move`;
    
    // Invalidate cache for this gathering and any related queries
    invalidateCache(`${API_BASE_URL}/gatherings/${gatheringId}`);
    invalidateCache(`${API_BASE_URL}/gatherings/search`);
    
    const response = await fetchWithTokenRefresh(url, token, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(moveData)
    });
    
    return response;
  } catch (error) {
    console.error('Error moving gathering:', error);
    throw error;
  }
};

/**
 * Fetches gatherings attended by a specific user
 * @param token Firebase authentication token
 * @param userId The ID of the user
 * @param fromDate Optional from_date filter (YYYY-MM-DD format)
 * @returns Promise containing an array of gatherings the user has attended
 */
export const fetchUserAttendedGatherings = async (
  token: string,
  userId: string,
  fromDate?: string
): Promise<Gathering[]> => {
  try {
    const searchParams = new URLSearchParams();
    
    // Add optional from_date filter
    if (fromDate) {
      searchParams.append('from_date', fromDate);
    }
    
    const queryString = searchParams.toString();
    const url = `${API_BASE_URL}/gatherings/admin/${userId}/attended${queryString ? `?${queryString}` : ''}`;
    const cacheKey = generateCacheKey(url, { userId, fromDate });
    
    // Check if we have a valid cached response
    const cachedData = getCacheItem<Gathering[]>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    // fetchWithTokenRefresh already returns the parsed JSON data
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response
    setCacheItem(cacheKey, data);
    
    return data;
  } catch (error) {
    console.error('Error fetching user attended gatherings:', error);
    throw error;
  }
};

/**
 * Updates a gathering's status as an admin, with optional notes
 * @param token Authentication token
 * @param gatheringId ID of the gathering to update
 * @param newStatus New status to set
 * @param notes Optional notes to include (required for NEED_MORE_INFO status)
 * @returns Promise that resolves when the update is complete
 */
export const updateGatheringStatusAsAdmin = async (
  token: string,
  gatheringId: string, 
  newStatus: GatheringStatus,
  notes?: string
): Promise<void> => {
  if (!token) {
    throw new Error('Authentication token is required');
  }

  try {
    // Invalidate cache for this gathering and any status-based queries
    invalidateCache(`${API_BASE_URL}/gatherings/${gatheringId}`);
    invalidateCache(`${API_BASE_URL}/gatherings/search`);
    
    // Get the enum key (e.g., "NEED_MORE_INFO") from the value ("Needs More Info")
    const statusKey = Object.keys(GatheringStatus).find(
      key => GatheringStatus[key as keyof typeof GatheringStatus] === newStatus
    );
    
    if (!statusKey) {
      throw new Error(`Invalid gathering status: ${newStatus}`);
    }
    
    // Build the URL for the admin endpoint
    const url = `${API_BASE_URL}/gatherings/${gatheringId}/admin/status`;

    const requestBody: { status: string; notes?: string } = {
      status: newStatus // Send the status value (e.g., "Need More Info")
    };
    
    // Only include notes if they're provided and status is either NEED_MORE_INFO or DENIED
    if (notes && (statusKey === 'NEED_MORE_INFO' || statusKey === 'DENIED' || statusKey=== 'RESCHEDULE')) {
      requestBody.notes = notes;
    }
    
    await fetchWithTokenRefresh(url, token, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });
    
    // Success - no need to return anything
  } catch (error: unknown) {
    console.error('Error updating gathering status as admin:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to update gathering status: ${error.message}`);
    } else {
      throw new Error(`Failed to update gathering status: ${String(error)}`);
    }
  }
};

/**
 * Transfers gathering ownership to a new user (admin only)
 * @param token Authentication token
 * @param gatheringId ID of the gathering to transfer
 * @param newOwnerId ID of the new owner user
 * @returns Promise containing the updated gathering
 */
export const transferGatheringOwnership = async (
  token: string,
  gatheringId: string,
  newOwnerId: string
): Promise<Gathering> => {
  if (!token) {
    throw new Error('Authentication token is required');
  }

  try {
    // Invalidate cache for this gathering and any related queries
    invalidateCache(`${API_BASE_URL}/gatherings/${gatheringId}`);
    invalidateCache(`${API_BASE_URL}/gatherings/search`);
    
    const url = `${API_BASE_URL}/gatherings/${gatheringId}/admin/owner`;
    
    const requestBody = {
      owner_id: newOwnerId
    };
    
    const response = await fetchWithTokenRefresh(url, token, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });
    
    return response;
  } catch (error: unknown) {
    console.error('Error transferring gathering ownership:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to transfer gathering ownership: ${error.message}`);
    } else {
      throw new Error(`Failed to transfer gathering ownership: ${String(error)}`);
    }
  }
};
